import { useState, type ReactNode } from "react";
import { Popover, Button } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { SearchOutlined } from "@mui/icons-material";
import { useWidth } from "@hooks";

/**
 * Envuelve el contenido de `renderTopToolbarCustomActions` de Material React
 * Table. En escritorio se renderiza tal cual. En móvil agrupa TODOS los
 * botones (refrescar, agregar, segmented de estado, exportar, PDF, Excel...)
 * dentro de un Popover corporativo disparado por un botón antd `default`
 * solo con icono, para que la barra de herramientas no empuje la tabla y la
 * paginación siga visible.
 *
 * Uso:
 *   renderTopToolbarCustomActions={({ table }) => (
 *     <MrtTopActions>
 *       ...botones...
 *     </MrtTopActions>
 *   )}
 */
export const MrtTopActions = ({
  children,
  isMobile: isMobileProp,
}: {
  children: ReactNode;
  /** Sobrescribe el corte por defecto (solo `xs`), p. ej. para colapsar también en sm/md. */
  isMobile?: boolean;
}) => {
  const width = useWidth();
  const isMobile = isMobileProp ?? width === "xs";
  const [open, setOpen] = useState(false);

  if (!isMobile) return <>{children}</>;

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 12, overflow: "hidden" }}
      content={
        <div style={{ minWidth: 260, maxWidth: "86vw" }}>
          <div
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid #f0f0f0",
              fontSize: 11,
              fontWeight: 700,
              color: "#1e1948",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            Herramientas
          </div>
          <div
            className="mrt-top-actions-popover"
            style={{
              padding: 12,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
            }}
          >
            {children}
          </div>
        </div>
      }
    >
      <Button
        type="default"
        icon={<MoreOutlined />}
        style={{
          height: 32,
          width: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      />
    </Popover>
  );
};

/**
 * Hook reutilizable para `muiSearchTextFieldProps`: resuelve el breakpoint
 * internamente, así el `page.tsx` no necesita cablear `useWidth`.
 * Uso: `muiSearchTextFieldProps={useMrtSearchTextFieldProps()}`
 */
export const useMrtSearchTextFieldProps = () => {
  const width = useWidth();
  return getMrtSearchTextFieldProps(width === "xs");
};

/**
 * Props reutilizables para `muiSearchTextFieldProps`: campo de búsqueda
 * compacto (más angosto en móvil, sin icono de lupa en móvil).
 */
export const getMrtSearchTextFieldProps = (isMobile: boolean) => ({
  placeholder: "Buscar...",
  size: "small" as const,
  variant: "outlined" as const,
  InputProps: {
    startAdornment: isMobile ? null : <SearchOutlined />,
  },
  style: {
    width: isMobile ? "160px" : "200px",
    minWidth: isMobile ? "160px" : "200px",
    maxWidth: isMobile ? "160px" : "200px",
    margin: "0px",
  },
});
